<!-- src/components/shared/CourseCard.vue -->
<template>
  <div class="course-card" @click="$emit('click', course.id)">
    <!-- Обложка -->
    <div class="course-image-wrapper">
      <img
        v-if="course.cover_image_url"
        :src="course.cover_image_url"
        :alt="course.title"
        class="course-image"
      />
      <div v-else class="course-image-placeholder">
        <svg viewBox="0 0 24 24" width="72" height="72">
          <rect x="3" y="3" width="18" height="18" rx="6" ry="6" fill="#e3f2ff" />
          <circle cx="12" cy="12" r="5" stroke="#0095ec" stroke-width="2" fill="none" />
        </svg>
      </div>
    </div>

    <div class="course-content">
      <!-- Метрики -->
      <div class="course-metrics">
        <div class="metric">
          <span class="metric-icon">📚</span>
          <span>{{ modulesText }}</span>
        </div>
        <div class="metric" v-if="course.duration_minutes">
          <span class="metric-icon">⏱️</span>
          <span>{{ durationText }}</span>
        </div>
      </div>

      <!-- Заголовок и описание -->
      <h3 class="course-title">{{ course.title }}</h3>
      <p class="course-description">
        {{ course.description || 'Описание отсутствует' }}
      </p>

      <!-- Преподаватель -->
      <div class="course-teacher">
        <div class="teacher-avatar">
          <img
            v-if="teacherAvatar"
            :src="teacherAvatar"
            :alt="course.teacher_name"
            class="teacher-avatar-img"
          />
          <span v-else class="teacher-initials">
            {{ teacherInitials }}
          </span>
        </div>
        <div class="teacher-info">
          <div class="teacher-label">Автор</div>
          <div class="teacher-name">
            {{ course.teacher_name || 'Не указан' }}
          </div>
        </div>
      </div>

      <!-- Рейтинг -->
      <div class="course-rating">
        <div class="stars">
          <template v-for="starIndex in 5">
            <svg
              v-if="getRatingStarType(course.rating, starIndex) === 'full'"
              :key="`full-${starIndex}`"
              class="rating-star"
              viewBox="0 0 19 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.14258 0.538086L11.292 6.47461C11.4224 6.83448 11.7391 7.12939 12.1543 7.16797H12.1553L17.7285 7.68066C17.7338 7.68601 17.7464 7.70159 17.7549 7.73145C17.7696 7.78365 17.7614 7.82638 17.7461 7.85449L17.7295 7.87695L13.4775 12.0586C13.2318 12.2993 13.122 12.6408 13.1504 12.9707L13.1709 13.1113L14.4697 19.3633C14.4827 19.4258 14.4619 19.4645 14.4463 19.4854L9.69043 16.1504L9.55859 16.0723C9.28789 15.9386 8.97481 15.9386 8.7041 16.0723L8.57227 16.1504L3.81543 19.4854C3.79989 19.4643 3.7801 19.4253 3.79297 19.3633L5.0918 13.1113C5.15999 12.787 5.0888 12.4351 4.88184 12.168L4.78516 12.0586L0.533203 7.87695C0.509346 7.85349 0.487369 7.80068 0.506836 7.73145C0.515011 7.70256 0.527625 7.68655 0.533203 7.68066L6.10742 7.16797H6.1084C6.52346 7.12939 6.83931 6.83434 6.96973 6.47461L9.12012 0.538086C9.12359 0.528544 9.12776 0.521361 9.13086 0.515625C9.13402 0.521415 9.13902 0.528316 9.14258 0.538086Z"
                fill="#FDB022"
                stroke="#FDB022"
              />
            </svg>
            <svg
              v-else-if="getRatingStarType(course.rating, starIndex) === 'half'"
              :key="`half-${starIndex}`"
              class="rating-star"
              viewBox="0 0 21 23"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.5 18.9283V0.5C10.4152 0.50001 10.3304 0.522091 10.2522 0.566244C10.0724 0.667865 9.942 0.993652 9.6815 1.64523L7.53241 7.01956C7.45531 7.21237 7.41675 7.30878 7.35825 7.38294C7.30647 7.44856 7.24358 7.50126 7.17347 7.5378C7.09425 7.57906 7.00285 7.59157 6.82003 7.61657L1.72477 8.31341C1.10701 8.3979 0.798142 8.44014 0.658765 8.60607C0.537642 8.75027 0.481103 8.95098 0.50562 9.14976C0.533822 9.37848 0.762177 9.62203 1.2189 10.1093L4.98596 14.1276L4.98732 14.129C5.12157 14.2723 5.18884 14.3441 5.23176 14.4309C5.26987 14.5081 5.29389 14.5934 5.30234 14.6815C5.31188 14.7812 5.29394 14.8853 5.25807 15.0936L4.2581 20.8986C4.13686 21.6024 4.07624 21.9543 4.16999 22.1585C4.25145 22.3359 4.39946 22.46 4.57093 22.4945C4.76825 22.5342 5.03965 22.359 5.58248 22.0085L10.0597 19.1177L10.0608 19.117C10.2208 19.0137 10.3008 18.962 10.3858 18.9418C10.4234 18.9328 10.4617 18.9283 10.5 18.9283Z"
                fill="#FDB022"
              />
              <path
                d="M10.3858 18.9418C10.4611 18.9237 10.5388 18.9237 10.6142 18.9418C10.6993 18.9621 10.7796 19.0139 10.9403 19.1177L15.4176 22.0085C15.9603 22.359 16.2318 22.5342 16.429 22.4945C16.6005 22.46 16.7486 22.3359 16.83 22.1585C16.9238 21.9543 16.8632 21.6024 16.7419 20.8986L15.742 15.0936C15.706 14.8853 15.6881 14.7812 15.6977 14.6815C15.7061 14.5934 15.7301 14.5081 15.7682 14.4309C15.8114 14.3439 15.8789 14.2719 16.0141 14.1276L19.7811 10.1093C20.2378 9.62203 20.4662 9.37848 20.4944 9.14976C20.5188 8.95098 20.4624 8.75027 20.3413 8.60607C20.2019 8.44014 19.8929 8.3979 19.2753 8.31341L14.18 7.61657C13.9972 7.59157 13.9057 7.57906 13.8265 7.5378C13.7564 7.50126 13.6936 7.44856 13.6417 7.38294C13.5833 7.30878 13.5447 7.21237 13.4675 7.01956L11.3185 1.64523C11.0579 0.993652 10.9277 0.667865 10.7478 0.566244C10.5914 0.477919 10.4086 0.477919 10.2522 0.566244M10.3858 18.9418C10.3008 18.962 10.2208 19.0137 10.0608 19.117L10.0597 19.1177L5.58248 22.0085C5.03965 22.359 4.76825 22.5342 4.57093 22.4945C4.39946 22.46 4.25145 22.3359 4.16999 22.1585C4.07624 21.9543 4.13686 21.6024 4.2581 20.8986L5.25807 15.0936C5.29394 14.8853 5.31188 14.7812 5.30234 14.6815C5.29389 14.5934 5.26987 14.5081 5.23176 14.4309C5.18884 14.3441 5.12157 14.2723 4.98732 14.129L4.98596 14.1276L1.2189 10.1093C0.762177 9.62203 0.533822 9.37848 0.50562 9.14976C0.481103 8.95098 0.537642 8.75027 0.658765 8.60607C0.798142 8.44014 1.10701 8.3979 1.72477 8.31341L6.82003 7.61657C7.00285 7.59157 7.09425 7.57906 7.17347 7.5378C7.24358 7.50126 7.30647 7.44856 7.35825 7.38294C7.41675 7.30878 7.45531 7.21237 7.53241 7.01956L9.6815 1.64523C9.942 0.993652 10.0724 0.667865 10.2522 0.566244M10.3858 18.9418C10.4234 18.9328 10.4617 18.9283 10.5 18.9283V0.5C10.4152 0.50001 10.3304 0.522091 10.2522 0.566244"
                stroke="#FDB022"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <svg
              v-else
              :key="`empty-${starIndex}`"
              class="rating-star"
              viewBox="0 0 19 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.14258 0.538086L11.292 6.47461C11.4224 6.83448 11.7391 7.12939 12.1543 7.16797H12.1553L17.7285 7.68066C17.7338 7.68601 17.7464 7.70159 17.7549 7.73145C17.7696 7.78365 17.7614 7.82638 17.7461 7.85449L17.7295 7.87695L13.4775 12.0586C13.2318 12.2993 13.122 12.6408 13.1504 12.9707L13.1709 13.1113L14.4697 19.3633C14.4827 19.4258 14.4619 19.4645 14.4463 19.4854L9.69043 16.1504L9.55859 16.0723C9.28789 15.9386 8.97481 15.9386 8.7041 16.0723L8.57227 16.1504L3.81543 19.4854C3.79989 19.4643 3.7801 19.4253 3.79297 19.3633L5.0918 13.1113C5.15999 12.787 5.0888 12.4351 4.88184 12.168L4.78516 12.0586L0.533203 7.87695C0.509346 7.85349 0.487369 7.80068 0.506836 7.73145C0.515011 7.70256 0.527625 7.68655 0.533203 7.68066L6.10742 7.16797H6.1084C6.52346 7.12939 6.83931 6.83434 6.96973 6.47461L9.12012 0.538086C9.12359 0.528544 9.12776 0.521361 9.13086 0.515625C9.13402 0.521415 9.13902 0.528316 9.14258 0.538086Z"
                stroke="#FDB022"
              />
            </svg>
          </template>
        </div>
        <div class="rating-text">
          <span class="rating-value">{{ ratingText }}</span>
          <span class="rating-count">({{ course.reviews_count || 0 }} отзывов)</span>
        </div>
      </div>

      <!-- Кнопка -->
      <button
        class="course-button"
        @click.stop="$emit('click', course.id)"
      >
        Подробнее
      </button>
    </div>
  </div>
</template>

<script>
import { DEMO_TEACHERS } from '@/utils/courseUtils'
import { formatDuration, formatRating, getInitials, pluralizeModule } from '@/utils/formatters'

export default {
  name: 'CourseCard',

  props: {
    course: {
      type: Object,
      required: true,
    },
  },

  emits: ['click'],

  computed: {
    modulesText() {
      const count = this.course.modules_count || 0
      return count ? `${count} ${pluralizeModule(count)}` : '0 модулей'
    },

    durationText() {
      return formatDuration(this.course.duration_minutes)
    },

    teacherAvatar() {
      if (this.course.teacher_avatar || this.course.teacher_avatar_url) {
        return this.course.teacher_avatar || this.course.teacher_avatar_url
      }
      const demoTeacher = DEMO_TEACHERS[this.course.teacher_name?.trim()]
      return demoTeacher?.avatar || null
    },

    teacherInitials() {
      return getInitials(this.course.teacher_name)
    },

    ratingText() {
      return formatRating(this.course.rating)
    },
  },
  methods: {
    getRatingStarType(rating, index) {
      const normalized =
        typeof rating === 'string' ? rating.replace(',', '.').trim() : rating
      const numericValue = Number.parseFloat(normalized)
      const safeValue = Number.isFinite(numericValue) ? numericValue : 0
      const fullStars = Math.floor(safeValue)
      const hasHalf = safeValue - fullStars >= 0.5
      if (index <= fullStars) return 'full'
      if (index === fullStars + 1 && hasHalf) return 'half'
      return 'empty'
    },
  },
}
</script>

<style scoped>
.course-card {
  flex: 0 0 385px;
  height: 697px;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid #e8f1fb;
  display: flex;
  flex-direction: column;
}

.course-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 24px rgba(0, 149, 236, 0.2);
}

.course-image-wrapper {
  width: 100%;
  padding: 16px 16px 0 16px;
  flex-shrink: 0;
}

.course-image {
  width: 100%;
  height: 220px;
  object-fit: cover;
  transition: transform 0.5s ease;
  display: block;
  border-radius: 16px;
}

.course-card:hover .course-image {
  transform: scale(1.05);
}

.course-image-placeholder {
  width: 100%;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f0f7ff 0%, #e6f0ff 100%);
  border-radius: 16px;
}

.course-content {
  padding: 20px 24px 24px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.course-metrics {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.metric {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #64748b;
}

.metric-icon {
  color: #0095ec;
}

.course-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 8px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.course-description {
  font-size: 14px;
  color: #64748b;
  margin: 0 0 auto 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
  word-break: break-word;
  overflow-wrap: break-word;
  min-height: 72px;
}

.course-teacher {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  margin-bottom: 10px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.teacher-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #0095ec;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
  overflow: hidden;
}

.teacher-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.teacher-initials {
  font-size: 12px;
}

.teacher-info {
  flex: 1;
}

.teacher-label {
  font-size: 11px;
  color: #94a3b8;
  margin: 0;
}

.teacher-name {
  font-size: 13px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
}

.course-rating {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 70px;
}

.stars {
  display: flex;
  gap: 2px;
}

.rating-star {
  width: 16px;
  height: 16px;
  display: block;
  flex: 0 0 auto;
}

.star {
  color: #e2e8f0;
  font-size: 16px;
  transition: color 0.2s ease;
}

.star.filled {
  color: #fbbf24;
}

.rating-text {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rating-value {
  font-weight: 700;
  color: #1a202c;
}

.rating-count {
  font-size: 12px;
  color: #94a3b8;
}

.course-button {
  width: 120px;
  height: 40px;
  padding: 0;
  background: #0095ec;
  color: white;
  border: none;
  border-radius: 36px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  margin-left: auto;
  margin-right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.course-button:hover {
  background: #0080cc;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 149, 236, 0.4);
}

.course-button:active {
  transform: translateY(0);
}
</style>
